import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./auth/AuthContext";
import AnalyticsBootstrap from "./components/AnalyticsBootstrap";
import { ProtectedRoute, RedirectIfAuthed } from "./components/ProtectedRoute";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ClientDashboard from "@/pages/ClientDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminClients from "@/pages/AdminClients";
import AdminLeads from "@/pages/AdminLeads";
import AdminCredit from "@/pages/AdminCredit";
import AdminFunding from "@/pages/AdminFunding";
import AdminTradelines from "@/pages/AdminTradelines";
import AdminGrants from "@/pages/AdminGrants";
import AdminDocuments from "@/pages/AdminDocuments";
import AdminReferrals from "@/pages/AdminReferrals";
import AdminRevenue from "@/pages/AdminRevenue";
import AdminCreditQueue from "@/pages/AdminCreditQueue";
import AdminDisputeQueue from "@/pages/AdminDisputeQueue";
import AdminCfpbQueue from "@/pages/AdminCfpbQueue";
import AdminArbitration from "@/pages/AdminArbitration";
import ClientCreditAnalyzer from "@/pages/ClientCreditAnalyzer";
import ClientDisputeTracker from "@/pages/ClientDisputeTracker";
import ClientFundingMatch from "@/pages/ClientFundingMatch";
import ClientJourney from "@/pages/ClientJourney";
import ClientProducts from "@/pages/ClientProducts";
import Privacy from "@/pages/Privacy";
import GlbaPrivacyNotice from "@/pages/GlbaPrivacyNotice";
import Pricing from "@/pages/Pricing";
import Terms from "@/pages/Terms";
import Refund from "@/pages/Refund";
import Checkout from "@/pages/Checkout";

const adminGuard = (el) => <ProtectedRoute role="admin">{el}</ProtectedRoute>;

function ScrollToHash() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    const id = hash.slice(1);
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (attempts < 20) {
        attempts += 1;
        setTimeout(tryScroll, 100);
      }
    };
    tryScroll();
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AnalyticsBootstrap />
          <ScrollToHash />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/pricing" element={<Pricing />} />

            {/* Auth */}
            <Route path="/dashboard" element={<RedirectIfAuthed><Login /></RedirectIfAuthed>} />
            <Route path="/dashboard/signup" element={<RedirectIfAuthed><Signup /></RedirectIfAuthed>} />
            <Route path="/dashboard/forgot-password" element={<ForgotPassword />} />
            <Route path="/dashboard/reset-password" element={<ResetPassword />} />

            {/* Protected — client */}
            <Route path="/dashboard/client" element={<ProtectedRoute role="client"><ClientDashboard /></ProtectedRoute>} />
            <Route path="/dashboard/client/profile-insights" element={<ProtectedRoute role="client"><ClientCreditAnalyzer /></ProtectedRoute>} />
            <Route path="/dashboard/client/credit-analyzer" element={<Navigate to="/dashboard/client/profile-insights" replace />} />
            <Route path="/dashboard/client/records-review" element={<ProtectedRoute role="client"><ClientDisputeTracker /></ProtectedRoute>} />
            <Route path="/dashboard/client/disputes" element={<Navigate to="/dashboard/client/records-review" replace />} />
            <Route path="/dashboard/client/funding-match" element={<ProtectedRoute role="client"><ClientFundingMatch /></ProtectedRoute>} />
            <Route path="/dashboard/client/journey" element={<ProtectedRoute role="client"><ClientJourney /></ProtectedRoute>} />
            <Route path="/dashboard/client/products" element={<ProtectedRoute role="client"><ClientProducts /></ProtectedRoute>} />

            {/* Protected — admin */}
            <Route path="/dashboard/admin" element={adminGuard(<AdminDashboard />)} />
            <Route path="/dashboard/admin/clients" element={adminGuard(<AdminClients />)} />
            <Route path="/dashboard/admin/leads" element={adminGuard(<AdminLeads />)} />
            <Route path="/dashboard/admin/credit" element={adminGuard(<AdminCredit />)} />
            <Route path="/dashboard/admin/credit-analyzer" element={adminGuard(<AdminCreditQueue />)} />
            <Route path="/dashboard/admin/disputes" element={adminGuard(<AdminDisputeQueue />)} />
            <Route path="/dashboard/admin/cfpb" element={adminGuard(<AdminCfpbQueue />)} />
            <Route path="/dashboard/admin/arbitration" element={adminGuard(<AdminArbitration />)} />
            <Route path="/dashboard/admin/funding" element={adminGuard(<AdminFunding />)} />
            <Route path="/dashboard/admin/tradelines" element={adminGuard(<AdminTradelines />)} />
            <Route path="/dashboard/admin/grants" element={adminGuard(<AdminGrants />)} />
            <Route path="/dashboard/admin/documents" element={adminGuard(<AdminDocuments />)} />
            <Route path="/dashboard/admin/referrals" element={adminGuard(<AdminReferrals />)} />
            <Route path="/dashboard/admin/revenue" element={adminGuard(<AdminRevenue />)} />

            {/* Legal */}
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/privacy/glba" element={<GlbaPrivacyNotice />} />
            {/* /ai-tools taken offline — page code kept in pages/AiTools.jsx for later restore */}
            <Route path="/ai-tools" element={<Navigate to="/" replace />} />
            <Route path="/ai-tools/*" element={<Navigate to="/" replace />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/refund" element={<Refund />} />
            <Route path="/checkout" element={<Checkout />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
